import { Container, Typography } from '@cleartrip/ct-design-components'
import { memo } from 'react'

import PerAdultTooltip from '@/components/srp/Tupple/Domestic/CommonComponents/PerAdultTooltip'
import { PaxCriteria } from '@/types/srp'
import { formatCurrency, isValidPrice } from '@/utils/general'

import type { TuplePrice } from '../model/types'
import RevampTooltip from './RevampTooltip'

export interface PriceBlockProps {
  price: TuplePrice
  density?: 'default' | 'compact'
  paxCriteria?: PaxCriteria[]
}

const CSS_COMPACT = { minWidth: 90, width: 'auto', flexShrink: 0 }
const CSS_DEFAULT = { minWidth: 180 }
const CSS_GAP8 = { columnGap: 8 }
const CSS_PER_ADULT = { alignSelf: 'flex-end' }

const PerAdultLabel: React.FC<{
  className?: string
  price: TuplePrice
  paxCriteria?: PaxCriteria[]
  withTooltip?: boolean
}> = ({ className = 'ta-right ws-nowrap', price, paxCriteria, withTooltip = false }) => {
  const label = (
    <Typography className={`${className}${withTooltip ? ' c-pointer' : ''}`} color='subheading' componentNode='p' variant='B3'>
      /adult
    </Typography>
  )

  if (
    !withTooltip ||
    !paxCriteria?.length ||
    price.totalBaseFare == null ||
    price.totalTax == null ||
    price.totalPrice == null
  ) {
    return label
  }

  return (
    <RevampTooltip
      align='center'
      content={
        <PerAdultTooltip
          paxCriteria={paxCriteria}
          totalBaseFare={price.totalBaseFare}
          totalPrice={price.totalPrice}
          totalTax={price.totalTax}
        />
      }
      padding={0}
      position='bottom'
      style={CSS_PER_ADULT}
      wrapperDisplay='inline-flex'
    >
      {label}
    </RevampTooltip>
  )
}

const PriceBlock: React.FC<PriceBlockProps> = ({ density = 'default', paxCriteria, price }) => {
  if (!isValidPrice(price.displayPrice)) return null

  const hasStrike = isValidPrice(price.strikeOffPrice) && (price.strikeOffPrice as number) > price.displayPrice
  const isCompact = density === 'compact'

  return (
    <Container
      className='p-relative flex flex-column flex-end flex-no-shrink'
      css={isCompact ? CSS_COMPACT : CSS_DEFAULT}
    >
      {isCompact ? (
        <>
          {hasStrike && (
            <Container style={{ position: 'absolute', top: -14, right: 0 }}>
              <Typography
                isStriked
                className='ta-right ws-nowrap fs-10'
                color='subheading'
                componentNode='span'
                variant='B4'
              >
                {formatCurrency(price.strikeOffPrice as number)}
              </Typography>
            </Container>
          )}
          <Typography className='ta-right ws-nowrap' color='heading' componentNode='p' variant='HM2'>
            {formatCurrency(price.displayPrice)}
          </Typography>
          {price.showPerAdult && (
            <PerAdultLabel className='ta-right ws-nowrap' price={price} />
          )}
        </>
      ) : (
        <>
          <Container className='flex flex-baseline flex-end' css={CSS_GAP8}>
            {hasStrike && (
              <Typography isStriked className='ws-nowrap' color='subheading' componentNode='span' variant='B3'>
                {formatCurrency(price.strikeOffPrice as number)}
              </Typography>
            )}
            <Typography className='ws-nowrap' color='heading' componentNode='p' variant='HM2'>
              {formatCurrency(price.displayPrice)}
            </Typography>
          </Container>
          {price.showPerAdult && (
            <PerAdultLabel paxCriteria={paxCriteria} price={price} withTooltip />
          )}
        </>
      )}
    </Container>
  )
}

export default memo(PriceBlock)
